// ═══════════════════════════════════════════════════════════════════
//  useAtlas — React hook for loading and querying an atlas manifest.
// ═══════════════════════════════════════════════════════════════════
//
//  Layer-1 loader: fetches manifest.json (~50-100 KB), validates it,
//  builds reverse-index. Layer-2 (document skeletons) and Layer-3
//  (full sheet content) are loaded by separate hooks/lazy effects.
//
//  Pattern matches existing FW hooks (useExplainBlock/Flow):
//    - State machine: idle → loading → loaded | error
//    - AbortController cancels in-flight requests on unmount
//    - JWT-aware (uses Supabase session if atlas is private)
//    - Memoized derived data (reverse-index, stats, grouped docs)
//
//  Usage:
//    const { atlas, isLoading, error, reverseIndex, stats } = useAtlas({
//      url: '/atlases/gotland-hub-atlas.json'
//    });
//
// ═══════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  Atlas,
  AtlasCategory,
  AtlasDocument,
  AtlasValidationResult,
  CrossDocumentRef,
  ReverseRefIndex,
} from '@/types/atlas';
import {
  buildForwardRefIndex,
  buildReverseRefIndex,
  computeAtlasStats,
  findDocument,
  getRefsToBlock,
  groupDocumentsByCategory,
  parseAtlas,
  resolveRefs,
  sortCategories,
  type AtlasStats,
  type ResolvedRef,
} from '@/lib/atlasLoader';

// ───────────────────────────────────────────────────────────────────
//  Types
// ───────────────────────────────────────────────────────────────────

export interface UseAtlasInput {
  /**
   * URL or path to atlas.json. Required unless `atlas` is provided directly.
   * Examples:
   *   - "/atlases/gotland-hub-atlas.json" (public folder)
   *   - "https://example.com/atlas.json" (remote)
   *   - "local:gotland-hub" (custom resolver — not implemented)
   */
  url?: string;

  /**
   * Pre-parsed atlas object. Skips fetching. Useful for testing or when
   * the manifest is bundled at build time.
   */
  atlas?: Atlas;

  /**
   * Whether to auto-fetch on mount. Default: true.
   * Set to false to manually trigger via `reload()`.
   */
  autoLoad?: boolean;

  /**
   * Whether to require authentication (Bearer token from Supabase session).
   * Default: false (atlases are public files by default).
   */
  requireAuth?: boolean;
}

export type AtlasLoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

export interface UseAtlasResult {
  /** Loaded atlas, or null if not loaded yet. */
  atlas: Atlas | null;

  /** Validation result from the loader. Null until first load completes. */
  validation: AtlasValidationResult | null;

  /** Loading state. */
  status: AtlasLoadStatus;

  /** Convenience boolean: status === 'loading'. */
  isLoading: boolean;

  /** Last error message, or null. */
  error: string | null;

  /**
   * Reverse-ref index: documentId → refs that POINT TO this document.
   * Memoized on `atlas.crossDocumentRefs` reference.
   */
  reverseIndex: ReverseRefIndex;

  /**
   * Forward-ref index: documentId → refs that ORIGINATE FROM this document.
   * Memoized.
   */
  forwardIndex: ReverseRefIndex;

  /** Aggregate stats (document count, block count, etc.). */
  stats: AtlasStats | null;

  /** Documents grouped by category. */
  documentsByCategory: Map<string, AtlasDocument[]>;

  /** Categories sorted for display. */
  sortedCategories: AtlasCategory[];

  // ── Lookup helpers ───────────────────────────────────────────────

  /** Find a document by ID. */
  findDoc: (documentId: string) => AtlasDocument | null;

  /** Get refs that target a specific block. */
  getBlockRefs: (documentId: string, blockId: string) => CrossDocumentRef[];

  /** Get refs originating from a specific block. */
  getOutgoingBlockRefs: (documentId: string, blockId: string) => CrossDocumentRef[];

  /** Resolve refs into navigation-ready objects. */
  resolveAllRefs: (refs: readonly CrossDocumentRef[]) => ResolvedRef[];

  // ── Actions ──────────────────────────────────────────────────────

  /** Manually re-fetch the atlas. */
  reload: () => Promise<void>;

  /** Reset to initial state. */
  reset: () => void;
}

// ───────────────────────────────────────────────────────────────────
//  Hook
// ───────────────────────────────────────────────────────────────────

export function useAtlas(input: UseAtlasInput = {}): UseAtlasResult {
  const { url, atlas: providedAtlas, autoLoad = true, requireAuth = false } = input;

  const [atlas, setAtlas] = useState<Atlas | null>(providedAtlas ?? null);
  const [validation, setValidation] = useState<AtlasValidationResult | null>(null);
  const [status, setStatus] = useState<AtlasLoadStatus>(
    providedAtlas ? 'loaded' : 'idle',
  );
  const [error, setError] = useState<string | null>(null);

  // AbortController for in-flight fetch (matches useExplainBlock/Flow pattern).
  const abortRef = useRef<AbortController | null>(null);

  // ── Fetch logic ──────────────────────────────────────────────────

  const fetchAtlas = useCallback(async (fetchUrl: string) => {
    // Cancel any in-flight request before starting a new one.
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setStatus('loading');
    setError(null);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };

      // Optional auth — for atlases stored behind RLS-protected endpoints.
      // We avoid importing supabase here unless requireAuth is true to keep
      // the hook usable in unauthenticated contexts (e.g., public atlases).
      if (requireAuth) {
        try {
          const { supabase } = await import('@/integrations/supabase/client');
          const { data: session } = await supabase.auth.getSession();
          const token = session.session?.access_token;
          if (!token) {
            setStatus('error');
            setError('not-authenticated');
            return;
          }
          headers.Authorization = `Bearer ${token}`;
        } catch {
          setStatus('error');
          setError('supabase-import-failed');
          return;
        }
      }

      const resp = await fetch(fetchUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      if (!resp.ok) {
        if (resp.status === 404) {
          setStatus('error');
          setError('atlas-not-found');
          return;
        }
        setStatus('error');
        setError(`http-${resp.status}`);
        return;
      }

      const raw = await resp.text();
      const { atlas: parsed, validation: parseValidation } = parseAtlas(raw);

      if (!parsed) {
        setStatus('error');
        setError(parseValidation.errors[0]?.message ?? 'parse-failed');
        setValidation(parseValidation);
        return;
      }

      // We accept atlases with warnings but not with errors.
      // UIs can still render an erroneous atlas if they want — just check
      // `validation.valid` themselves.
      setAtlas(parsed);
      setValidation(parseValidation);
      setStatus('loaded');
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setStatus('error');
      setError((e as Error).message ?? 'network-error');
    }
  }, [requireAuth]);

  const reload = useCallback(async () => {
    if (!url) {
      setError('no-url-provided');
      setStatus('error');
      return;
    }
    await fetchAtlas(url);
  }, [url, fetchAtlas]);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setAtlas(null);
    setValidation(null);
    setStatus('idle');
    setError(null);
  }, []);

  // ── Auto-load on mount + URL change ──────────────────────────────

  useEffect(() => {
    if (providedAtlas) {
      // If atlas object is provided directly, use it without fetching.
      setAtlas(providedAtlas);
      setStatus('loaded');
      return;
    }
    if (autoLoad && url) {
      void fetchAtlas(url);
    }
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, [url, autoLoad, providedAtlas, fetchAtlas]);

  // ── Memoized derived data ────────────────────────────────────────

  const reverseIndex = useMemo<ReverseRefIndex>(() => {
    if (!atlas) return new Map();
    return buildReverseRefIndex(atlas.crossDocumentRefs);
  }, [atlas]);

  const forwardIndex = useMemo<ReverseRefIndex>(() => {
    if (!atlas) return new Map();
    return buildForwardRefIndex(atlas.crossDocumentRefs);
  }, [atlas]);

  const stats = useMemo<AtlasStats | null>(() => {
    if (!atlas) return null;
    return computeAtlasStats(atlas);
  }, [atlas]);

  const documentsByCategory = useMemo(() => {
    if (!atlas) return new Map<string, AtlasDocument[]>();
    return groupDocumentsByCategory(atlas);
  }, [atlas]);

  const sortedCategories = useMemo(() => {
    if (!atlas) return [];
    return sortCategories(atlas.categories);
  }, [atlas]);

  // ── Lookup helpers (closures over atlas) ─────────────────────────

  const findDoc = useCallback(
    (documentId: string): AtlasDocument | null => {
      if (!atlas) return null;
      return findDocument(atlas, documentId);
    },
    [atlas],
  );

  const getBlockRefs = useCallback(
    (documentId: string, blockId: string): CrossDocumentRef[] => {
      return getRefsToBlock(reverseIndex, documentId, blockId);
    },
    [reverseIndex],
  );

  const getOutgoingBlockRefs = useCallback(
    (documentId: string, blockId: string): CrossDocumentRef[] => {
      const docRefs = forwardIndex.get(documentId) ?? [];
      return docRefs.filter((r) => r.fromBlockId === blockId);
    },
    [forwardIndex],
  );

  const resolveAllRefs = useCallback(
    (refs: readonly CrossDocumentRef[]): ResolvedRef[] => {
      if (!atlas) return [];
      return resolveRefs(atlas, refs);
    },
    [atlas],
  );

  return {
    atlas,
    validation,
    status,
    isLoading: status === 'loading',
    error,
    reverseIndex,
    forwardIndex,
    stats,
    documentsByCategory,
    sortedCategories,
    findDoc,
    getBlockRefs,
    getOutgoingBlockRefs,
    resolveAllRefs,
    reload,
    reset,
  };
}
